import { useAuth } from '@clerk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useResetRecoilState } from 'recoil';

import { getPeopleFindOptions } from 'client/@tanstack/react-query.gen';
import type {
  GetPeopleFindData,
  GetPeopleFindResponse,
  PersonRole,
  PersonV1CurrentResponse,
} from 'client/types.gen';
import { atomForm, atomModal, atomRecommendations } from 'config/atoms';
import { setUser } from 'helpers/logging';
import { useQueryData, useSearch } from 'helpers/search';
import type { UseSearch } from 'types/search';

export const defaultCountry = 'United States';

export const getNameInitial = (name: string) => name.slice(0, 1);

export const getPersonName = (
  person?:
    | Partial<Pick<PersonV1CurrentResponse, 'first_name' | 'last_name'>>
    | Partial<{ candidate_name: string | null | undefined }>
    | null,
) => {
  if (!person) {
    return '';
  }

  if ('candidate_name' in person && person.candidate_name) {
    return person.candidate_name;
  }

  // TODO: Kept for legacy API compatibility
  if ('person_name' in person && person.person_name) {
    return person.person_name as string;
  }

  const firstName = ('first_name' in person ? person.first_name : '') ?? '';
  const lastName = ('last_name' in person ? person.last_name : '') ?? '';

  return lastName ? `${firstName} ${lastName}` : firstName;
};

/**
 * Whether the current user is an activated executive client.
 *
 * `people/current` deliberately strips the EXECUTIVE role from `roles` (it is
 * an identity marker, not a team/org role) and exposes `is_executive` instead
 * — never check `roles` for the "Executive Client" title; it cannot appear.
 */
export const isPersonExecutive = (
  user?: PersonV1CurrentResponse | null,
): boolean => user?.is_executive === true;

export const getPersonRole = (
  person?: PersonV1CurrentResponse,
  teamSlug?: string,
) => {
  if (!person) {
    return undefined;
  }

  const adminRole = isInternalPerson(person);

  if (adminRole) {
    return adminRole;
  }

  if (!teamSlug) {
    // the API already sorts archived teams last; keep the guard here too so a
    // stale payload cannot land the person on a team that is closed to them
    return (
      person.roles?.find((role) => role.team_active !== false) ??
      person.roles?.[0]
    );
  }

  return person.roles?.find((role) => role.team_slug === teamSlug);
};

/**
 * At least one person role includes a company (slug), e.g. for share / team flows.
 */
export function userHasCompanyOnRoles(
  user: PersonV1CurrentResponse | undefined,
): boolean {
  return (user?.roles ?? []).some((r) => Boolean(r.company?.slug));
}

/**
 * Whether the external team create flow can call a valid API path
 * (nested team under an existing team, or company-scoped create).
 */
export function canOpenExternalTeamCreateForm(
  user: PersonV1CurrentResponse | undefined,
  role: PersonRole | undefined,
  teamsCount: number,
): boolean {
  if (teamsCount > 0) {
    return true;
  }
  if (role?.team_slug) {
    return true;
  }
  return userHasCompanyOnRoles(user);
}

export const getRolesMoveTeam = (
  roles: Required<PersonV1CurrentResponse>['roles'],
  teamSlug: string,
  index: number,
): Required<PersonV1CurrentResponse>['roles'] => {
  let team: Required<PersonV1CurrentResponse>['roles'][number];

  const rolesWithoutTeam = roles.filter((item) => {
    if (item.team_slug === teamSlug) {
      team = item;
      return false;
    }
    return true;
  });

  if (index === -1) {
    return rolesWithoutTeam;
  }

  return [
    ...rolesWithoutTeam.slice(0, index),
    team!,
    ...rolesWithoutTeam.slice(index),
  ];
};

export const isInternalPerson = (person?: PersonV1CurrentResponse) =>
  (person?.roles ?? []).find((role) => role.title === 'Banff Admin');

export const showPostalCode = (country?: string) => country === defaultCountry;

export const useOnSuccessLogOut = () => {
  const queryClient = useQueryClient();
  const auth = useAuth();

  const resetForm = useResetRecoilState(atomForm);
  const resetModal = useResetRecoilState(atomModal);
  const resetRecommendations = useResetRecoilState(atomRecommendations);

  const onSuccessLogOut = useCallback(async () => {
    // Sign out Clerk session
    auth.signOut();
    // TODO: We reset every state except `redirectIgnore`.
    // There should be an API to abstract resetting, Recoil is working on memory management.
    // learn more: https://github.com/facebookexperimental/Recoil/issues/387#issuecomment-658297951
    resetForm();
    resetModal();
    resetRecommendations();
    setUser();
    queryClient.clear();
  }, [queryClient, resetForm, resetModal, resetRecommendations, auth]);

  return {
    onSuccessLogOut,
  };
};

export const useSearchPeople: UseSearch<
  GetPeopleFindResponse,
  Omit<GetPeopleFindData, 'url'>
> = (opts, p) => {
  const { debouncedPhrase, enabled, phrase, ...search } = useSearch(opts);
  const query = useQuery({
    enabled,
    ...getPeopleFindOptions({
      ...p,
      query: {
        ...p?.query,
        phrase: opts?.debounce ? debouncedPhrase : phrase,
      },
    }),
  });
  const queryDataProps = useQueryData({
    ...opts,
    phrase,
    predicate: (person) => person.id,
    query,
  });

  return {
    // eslint-disable-next-line @tanstack/query/no-rest-destructuring
    ...query,
    ...search,
    ...queryDataProps,
    debouncedPhrase,
    phrase,
  };
};
